import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { BrandEmblem } from "../components/portal/BrandEmblem";
import { Loader2, ShieldCheck } from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1B2B] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-[#D4AF37]/30 p-8 md:p-10">
        <div className="flex items-center gap-3">
          <BrandEmblem />
          <div className="font-serif text-lg font-semibold text-[#0B1B2B]">Acuity Rise Portal</div>
        </div>
        {done ? (
          <div className="mt-8 text-center py-4" data-testid="reset-success">
            <ShieldCheck size={32} className="text-[#D4AF37] mx-auto" />
            <h1 className="mt-4 font-serif text-2xl text-[#0B1B2B]">Password Updated</h1>
            <p className="mt-2 text-sm text-[#64748B]">Your new password is active. You may now sign in.</p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 bg-[#0B1B2B] text-white font-medium px-8 py-3 rounded-xl hover:bg-[#152a40] transition-colors"
              data-testid="reset-goto-login"
            >
              Go to Portal
            </button>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-serif text-2xl text-[#0B1B2B]">Set a New Password</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input className={inputCls} type="password" placeholder="New password (min 10 chars, upper, lower & number)" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
              <input className={inputCls} type="password" placeholder="Confirm new password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={busy || !token} className="w-full bg-[#D4AF37] text-[#0B1B2B] font-semibold py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="reset-submit-button">
                {busy && <Loader2 size={16} className="animate-spin" />}
                Update Password
              </button>
              {!token && <p className="text-xs text-red-600">This reset link is invalid — request a new one from the sign-in screen.</p>}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
